import type { YammerCache } from './types';

interface CacheItem {
  readonly value: unknown;
  readonly expiry: number;
  readonly ttl: number;
}

export default class CacheWithExpiration implements YammerCache {
  private storage: YammerCache;
  private keyNamespace?: string;
  private excludeNamespaceFromKey?: boolean;

  constructor(storage: YammerCache, keyNamespace?: string, excludeNamespaceFromKey?: boolean) {
    this.storage = storage;
    this.keyNamespace = keyNamespace;
    this.excludeNamespaceFromKey = excludeNamespaceFromKey;
  }

  public async getItem<T>(key: string): Promise<T | null> {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    const cachedItem = await this.storage.getItem<CacheItem>(keyWithNamespace);

    if (!cachedItem) {
      return null;
    }

    const { value, expiry } = cachedItem;

    if (expiry > 0 && expiry < Date.now()) {
      await this.removeItem(keyWithNamespace);

      return null;
    }

    return value as T;
  }

  public async getTimeInCache(key: string): Promise<number | null> {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    const cachedItem = await this.storage.getItem<CacheItem>(keyWithNamespace);

    if (!cachedItem) {
      return null;
    }

    const { expiry, ttl } = cachedItem;

    const hasTTL = !!ttl;
    const isExpired = expiry > 0 && expiry < Date.now();

    if (!hasTTL || isExpired) {
      return null;
    }

    return Date.now() - (expiry - ttl);
  }

  public async setItem<T>(key: string, value: T, ttlInSeconds?: number): Promise<void> {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    const ttl = ttlInSeconds ? ttlInSeconds * 1000 : 0;
    const expiry = ttlInSeconds ? Date.now() + ttl : 0;
    await this.storage.setItem(keyWithNamespace, { value, expiry, ttl });
  }

  public async removeItem(key: string): Promise<void> {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    await this.storage.removeItem(keyWithNamespace);
  }

  public async clear(): Promise<void> {
    await this.storage.clear();
  }

  public async getAllKeys(): Promise<string[]> {
    if (!this.storage.getAllKeys) {
      return [];
    }

    return await this.storage.getAllKeys();
  }

  public async deleteKeys(keys: string[]): Promise<void> {
    if (!this.storage.deleteKeys) {
      return;
    }
    await this.storage.deleteKeys(keys);
  }

  private getKeyWithNamespace(key: string): string {
    return this.keyNamespace && !this.excludeNamespaceFromKey ? `${this.keyNamespace}::${key}` : key;
  }
}
